import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSync, faExclamationTriangle, faCheckCircle,
  faClock, faTimesCircle, faSave, faExternalLinkAlt,
  faUser, faCalendarAlt, faHourglassHalf, faUpload,
} from '@fortawesome/free-solid-svg-icons';
import {
  obtenerPublicacionWeb,
  guardarPublicacionWeb,
  sincronizarWooCommerce,
  subirImagenWeb,
} from '../../../../actions/modulosActions';

const MODALIDADES = [
  { value: 'en_vivo', label: 'En vivo' },
  { value: 'grabado', label: 'Grabado' },
];
const CATEGORIAS = ['Tributación', 'Contabilidad', 'Minería', 'Laboral', 'Otros'];

const estadoBadge = (status) => {
  const map = {
    publicado:    { variant: 'success',   icon: faCheckCircle,        label: 'Publicado'     },
    error:        { variant: 'danger',    icon: faTimesCircle,        label: 'Error'         },
    pendiente:    { variant: 'warning',   icon: faClock,              label: 'Pendiente'     },
    no_publicado: { variant: 'secondary', icon: faExclamationTriangle, label: 'No publicado' },
  };
  const cfg = map[status] || map['no_publicado'];
  return (
    <Badge variant={cfg.variant} style={{ fontSize: 13, padding: '5px 10px' }}>
      <FontAwesomeIcon icon={cfg.icon} className="mr-1" />
      {cfg.label}
    </Badge>
  );
};

const InfoItem = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
    <FontAwesomeIcon icon={icon} style={{ color: '#6c757d', marginTop: 2, width: 14 }} />
    <div>
      <div style={{ fontSize: 11, color: '#999', lineHeight: 1 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500, color: '#444' }}>{value || <em style={{ color: '#ccc' }}>Sin datos</em>}</div>
    </div>
  </div>
);

const initialForm = {
  publicar_catalogo: 0,
  precio_web:        '',
  imagen_web:        '',
  modalidad:         '',
  categoria_web:     '',
  descripcion_corta: '',
};

const TabPublicacionWeb = ({ idModulo }) => {
  const dispatch = useDispatch();

  const [pub,     setPub]     = useState(null);
  const [modData, setModData] = useState(null);
  const [form,    setForm]    = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(false);
  const [errores, setErrores] = useState([]);

  const cargar = async () => {
    const { pub: p, modulo: m } = await dispatch(obtenerPublicacionWeb(idModulo));
    setPub(p);
    setModData(m);
    if (p) {
      setForm({
        publicar_catalogo: p.publicar_catalogo || 0,
        precio_web:        p.precio_web        || '',
        imagen_web:        p.imagen_web        || '',
        modalidad:         p.modalidad         || '',
        categoria_web:     p.categoria_web     || '',
        descripcion_corta: p.descripcion_corta || '',
      });
    }
  };

  useEffect(() => {
    if (!idModulo) return;
    setLoading(true);
    cargar().finally(() => setLoading(false));
  }, [idModulo]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };

  const yaPublicado = !!pub?.woo_product_id;
  const syncStatus  = pub?.sync_status || 'no_publicado';

  const handleGuardar = async () => {
    setBusy(true);
    setErrores([]);

    const ok = await dispatch(guardarPublicacionWeb(idModulo, form));
    if (!ok) { setBusy(false); return; }

    if (yaPublicado) {
      // Ya publicado → auto-sincroniza al guardar
      const result = await dispatch(sincronizarWooCommerce(idModulo));
      if (!result.ok) {
        setErrores(result.errores || [result.error || 'Error al sincronizar']);
      }
    }

    await cargar();
    setBusy(false);
  };

  const handlePublicar = async () => {
    setBusy(true);
    setErrores([]);
    const result = await dispatch(sincronizarWooCommerce(idModulo));
    if (!result.ok) {
      setErrores(result.errores || [result.error || 'Error al publicar']);
    }
    await cargar();
    setBusy(false);
  };

  if (loading) {
    return <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>;
  }

  return (
    <div style={{ padding: '20px 4px' }}>

      {/* ── Datos automáticos del subproducto ── */}
      <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#5a7fcc', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
          Datos del subproducto (automáticos)
        </div>
        <Row>
          <Col md={4}>
            <InfoItem icon={faUser}          label="Docente"       value={modData?.docente}      />
          </Col>
          <Col md={4}>
            <InfoItem icon={faHourglassHalf} label="Duración"      value={modData?.duracion}     />
          </Col>
          <Col md={4}>
            <InfoItem icon={faCalendarAlt}   label="Fecha de inicio" value={modData?.fecha_inicio} />
          </Col>
        </Row>
      </div>

      {/* ── Estado ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Estado de publicación</div>
          {estadoBadge(syncStatus)}
          {pub?.last_sync_at && (
            <span style={{ marginLeft: 12, fontSize: 11, color: '#aaa' }}>
              Última sync: {pub.last_sync_at}
            </span>
          )}
          {yaPublicado && (
            <span style={{ marginLeft: 10, fontSize: 11, color: '#6c757d' }}>
              · Al guardar se sincroniza automáticamente
            </span>
          )}
        </div>
        {pub?.public_url && (
          <a href={pub.public_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-success">
            <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" /> Ver en web
          </a>
        )}
      </div>

      {/* ── Error previo de sync ── */}
      {syncStatus === 'error' && pub?.sync_error_message && (
        <Alert variant="danger" style={{ fontSize: 12 }}>
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          {pub.sync_error_message}
        </Alert>
      )}

      {/* ── Errores de validación ── */}
      {errores.length > 0 && (
        <Alert variant="warning">
          <strong>Corrige estos campos antes de publicar:</strong>
          <ul className="mb-0 mt-1">
            {errores.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </Alert>
      )}

      {/* ── Formulario ── */}
      <Form>
        <Form.Group>
          <Form.Check
            type="switch"
            id="publicar_catalogo"
            name="publicar_catalogo"
            label={<strong>Publicar en catálogo web</strong>}
            checked={!!form.publicar_catalogo}
            onChange={handleChange}
          />
          <Form.Text className="text-muted">
            Activa para que el curso aparezca en <em>inedi.edu.pe</em>. Desactiva para ocultarlo sin borrar la información.
          </Form.Text>
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Precio web (S/.)</Form.Label>
              <Form.Control
                type="number"
                name="precio_web"
                value={form.precio_web}
                onChange={handleChange}
                placeholder="Ej: 350"
                min="0"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Modalidad</Form.Label>
              <Form.Control as="select" name="modalidad" value={form.modalidad} onChange={handleChange}>
                <option value="">-- Seleccionar --</option>
                {MODALIDADES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Categoría web</Form.Label>
              <Form.Control as="select" name="categoria_web" value={form.categoria_web} onChange={handleChange}>
                <option value="">-- Seleccionar --</option>
                {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Imagen principal</Form.Label>
              <ImagenWebUploader
                idModulo={modData?.id_modulo}
                urlActual={form.imagen_web}
                onUrlCambiada={url => setForm(f => ({ ...f, imagen_web: url }))}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group>
          <Form.Label>
            Descripción corta <small className="text-muted">(aparece en la card del catálogo)</small>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="descripcion_corta"
            value={form.descripcion_corta}
            onChange={handleChange}
            placeholder="Descripción breve del curso para mostrar en el catálogo web..."
            maxLength={300}
          />
          <Form.Text className="text-muted">{form.descripcion_corta.length}/300 caracteres</Form.Text>
        </Form.Group>

        {pub?.woo_product_id && (
          <Form.Group>
            <Form.Label>ID WooCommerce</Form.Label>
            <Form.Control
              type="text"
              value={pub.woo_product_id}
              readOnly
              style={{ background: '#f8f9fa', fontFamily: 'monospace', maxWidth: 160 }}
            />
          </Form.Group>
        )}
      </Form>

      {/* ── Botones ── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #dee2e6', flexWrap: 'wrap' }}>

        {/* Guardar — siempre visible */}
        <Button variant="primary" onClick={handleGuardar} disabled={busy}>
          {busy && yaPublicado
            ? <><Spinner animation="border" size="sm" className="mr-1" /> Guardando y sincronizando...</>
            : busy
            ? <><Spinner animation="border" size="sm" className="mr-1" /> Guardando...</>
            : <><FontAwesomeIcon icon={faSave} className="mr-1" />
                {yaPublicado ? 'Guardar y sincronizar' : 'Guardar configuración'}</>
          }
        </Button>

        {/* Publicar — solo si aún no está publicado */}
        {!yaPublicado && (
          <Button variant="success" onClick={handlePublicar} disabled={busy}>
            {busy
              ? <><Spinner animation="border" size="sm" className="mr-1" /> Publicando...</>
              : <><FontAwesomeIcon icon={faUpload} className="mr-1" /> Publicar en WordPress</>
            }
          </Button>
        )}

        {/* Re-sincronizar — solo si ya está publicado y hay error */}
        {yaPublicado && syncStatus === 'error' && (
          <Button variant="outline-danger" onClick={handlePublicar} disabled={busy}>
            <FontAwesomeIcon icon={faSync} className="mr-1" /> Reintentar publicación
          </Button>
        )}
      </div>
    </div>
  );
};

export default TabPublicacionWeb;

function ImagenWebUploader({ idModulo, urlActual, onUrlCambiada }) {
  const dispatch = useDispatch();
  const [subiendo, setSubiendo] = useState(false);
  const inputRef = React.useRef();

  const handleFile = async (e) => {
    const archivo = e.target.files[0];
    if (!archivo) return;
    setSubiendo(true);
    const url = await dispatch(subirImagenWeb(idModulo, archivo));
    setSubiendo(false);
    if (url) onUrlCambiada(url);
    e.target.value = '';
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={subiendo || !idModulo}
          onClick={() => inputRef.current.click()}
        >
          {subiendo
            ? <><Spinner as="span" animation="border" size="sm" /> Subiendo...</>
            : <><FontAwesomeIcon icon={faUpload} className="mr-1" /> Seleccionar imagen</>
          }
        </Button>
        {urlActual && (
          <small className="text-muted text-truncate" style={{ maxWidth: 200 }}>
            {urlActual.split('/').pop()}
          </small>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleFile}
      />
      {urlActual && (
        <img
          src={urlActual}
          alt="preview"
          style={{ marginTop: 8, maxHeight: 100, borderRadius: 4, border: '1px solid #dee2e6', display: 'block' }}
          onError={e => e.target.style.display = 'none'}
        />
      )}
    </div>
  );
}
